<template>
  <div class="personal-info-container">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <div v-else class="forms">
        <form-group
          ref="formGroup"
          class="form"
          :label-left="!isMobile"
          :schemas="personalInfo"
          :data="data"
          :options="options"
          :disabled="submitting"
          :required-fields="requiredFields"
          @sync:personal="onSyncPersonal"
        />

        <form-group
          v-if="!isNotForeign"
          ref="formGroupForeign"
          class="form"
          :label-left="!isMobile"
          :schemas="personalInfoForeign"
          :data="dataPersonalForeign"
          :options="options"
          :disabled="submitting"
          :required-fields="requiredFields"
          @sync:foreign="onSyncPersonalForeign"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import * as types from '@types'
import FormGroup from '@components/FormGroup'
import personalSchemas, { isNotForeign } from '@forms/personalInfo'
import { MediaQuery } from '@convenia/mixins'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'PersonalInfo',

  mixins: [ MediaQuery ],

  components: {
    FormGroup
  },

  computed: {
    ...mapState({
      personal: state => state.personalInfo.personal,
      foreign: state => state.personalInfo.foreign,
      handicap: state => state.personalInfo.handicap,
      countries: state => state.location.countries,
      states: state => state.location.states,
      customFields: state => state.personalInfo.customFields
    }),

    ...mapGetters({
      getRequiredFields: types.EMPLOYEE_GET_REQUIRED_SYSTEM_FIELDS
    }),

    options () {
      const { options } = this.$store.state.personalInfo || {}
      const { residenceTime } = (this.foreignData || {})

      const undetermined = [ 1, 2, 3, 8 ]
      const determined = [ 4, 5, 2, 6, 7, 8 ]
      const { entryConditions } = options?.personal || {}

      const entryOptions = entryConditions?.reduce((acc, cur) => {
        if (residenceTime === 1 && undetermined.includes(cur.id))
          acc.push(cur)
        if (residenceTime === 2 && determined.includes(cur.id))
          acc.push(cur)
        return acc
      }, [])

      return {
        ...options,
        foreign: {
          ...options.personal,
          countries: this.countries,
          entryConditionId: entryOptions
        },
        personal: {
          ...options.personal,
          countries: this.countries,
          states: this.states,
          cities: this.cities
        }
      }
    },

    data () {
      return {
        personal: { ...this.personal },
        handicap: { ...this.handicap, hasDeficiency: !!this.handicap.id },
      }
    },

    dataPersonalForeign () {
      return {
        foreign: { ...this.foreign },
      }
    },

    personalInfo () {
      const { personal, handicap } = personalSchemas(this)

      // Handicap dynamic disable
      const customHandicapFields = Object.entries(this.customFields.handicap.schema)
        .reduce((acc, [ key, value ]) => ({
          ...acc,
          [key]: { ...value, isDisabled: formData => !formData.hasDeficiency }
        }), {})

      const personalFields = { ...personal.fields, ...this.customFields?.personal?.schema || {} }
      const handicapFields = { ...handicap.fields, ...customHandicapFields }

      return {
        personal: { ...personal, fields: personalFields },
        handicap: { ...handicap, fields: handicapFields },
      }
    },

    personalInfoForeign () {
      const { foreign } = personalSchemas(this)

      const foreignFields = { ...foreign.fields, ...this.customFields?.foreign?.schema || {} }

      return {
        foreign: { ...foreign, fields: foreignFields }
      }
    },

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    },

    requiredFields () {
      return [
        ...this.personalInfoRequiredFields,
        ...this.getRequiredFields('ForeignEmployee'),
        ...this.getRequiredFields('Handicapped'),
      ]
    },

    personalInfoRequiredFields () {
      const filterOut = this.isNotForeign ? [] : [ 'city', 'state' ]

      return [
        ...this.getRequiredFields('PersonalInformation', filterOut),
        ...this.getRequiredFields('Nationality'),
        ...this.getRequiredFields('Employee'),
      ]
    }
  },

  data: () => ({
    loading: true,
    cities: [],
    isNotForeign: true,
    foreignData: null
  }),

  mounted () {
    this.$root.$on('next', this.onSubmit)
    this.$root.$on('previous', this.onSave)
    this.init()
  },

  beforeDestroy () {
    this.$root.$off('next', this.onSubmit)
    this.$root.$off('previous', this.onSave)
  },

  methods: {
    ...mapMutations({
      savePersonal: types.EMPLOYEE_UPDATE_PERSONAL,
      saveForeign: types.EMPLOYEE_UPDATE_FOREIGN,
      saveHandicap: types.EMPLOYEE_UPDATE_HANDICAP,
    }),

    ...mapActions({
      getCustomFields: types.EMPLOYEE_PERSONAL_CUSTOM_FIELDS,
      getOptions: types.EMPLOYEE_PERSONAL_OPTIONS,
      getPersonal: types.EMPLOYEE_PERSONAL_INFO,
      getHandicap: types.EMPLOYEE_GET_HANDICAP,
      getForeign: types.EMPLOYEE_GET_FOREIGN,
      getCountries: types.LOCATION_COUNTRIES,
      getStates: types.LOCATION_STATES,
      getCities: types.LOCATION_CITIES,
      updatePersonal: types.EMPLOYEE_UPDATE_PERSONAL,
      updateEmployee: types.EMPLOYEE_UPDATE_EMPLOYEE,
      updateNationalities: types.EMPLOYEE_UPDATE_NATIONALITIES,
      updateForeign: types.EMPLOYEE_UPDATE_FOREIGN,
      updateHandicap: types.EMPLOYEE_UPDATE_HANDICAP,
      deleteHandicap: types.EMPLOYEE_DELETE_HANDICAP,
      getRequiredSystemFields: types.EMPLOYEE_GET_REQUIRED_SYSTEM_FIELDS,
    }),

    async init () {
      await Promise.allSettled([
        this.getOptions(),
        this.getPersonal(),
        this.getHandicap(),
        this.getForeign(),
        this.getCountries(),
        this.getStates(),
      ])

      await this.getCustomFields()

      if (this.personal.birthStateId)
        this.cities = await this.getCities(this.personal.birthStateId)

      this.loading = false
    },

    async validate () {
      await this.getRequiredSystemFields()

      if (this.$refs.formGroup && !await this.$refs.formGroup.validate())
        return false

      if (this.$refs.formGroupForeign && !await this.$refs.formGroupForeign?.validate())
        return false

      return true
    },

    async onSubmit () {
      if (!await this.validate()) return

      const data = {
        ...this.$refs.formGroup?.formsData || {},
        ...this.$refs.formGroupForeign?.formsData || {}
      }

      this.submitting = true

      const requests = [
        this.updatePersonal(data.personal),
        this.updateEmployee(data.personal),
        this.updateNationalities(data.personal),
      ]

      if (!this.isNotForeign)
        requests.push(this.updateForeign(data.foreign))

      if (data.handicap?.hasDeficiency)
        requests.push(this.updateHandicap(data.handicap))
      else if (this.handicap?.id)
        requests.push(this.deleteHandicap())

      const results = await Promise.allSettled(requests)

      this.submitting = false

      const hasErrors = results.some(({ value: [ err ] = [] } = {}) => !!err)

      if (hasErrors)
        return this.$feedback.add({
          type: 'error',
          message: 'Erro',
          highlighted: 'ao salvar Dados Pessoais. Tente novamente.'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Informações pessoais',
        highlighted: 'atualizadas com sucesso'
      })

      this.$emit('next')
    },

    async onSave () {
      if (!this.$refs.formGroup) return

      const data = this.$refs.formGroup.formsData

      await this.savePersonal({ ...this.personal, ...data.personal })

      this.saveForeign({ ...this.foreign, ...data.foreign })
      this.saveHandicap({ ...this.handicap, ...data.handicap })

      this.$emit('previous')
    },

    onSyncPersonal (personal) {
      this.isNotForeign = isNotForeign(personal)
    },

    onSyncPersonalForeign (foreign) {
      this.foreignData = foreign
    }
  },

  watch: {
    data (data) {
      this.onSyncPersonal(data.personal)
    }
  }
}
</script>

<style lang="scss">
.personal-info-container {
  position: relative;
  & > .forms > .form {
    @include responsive (tablet, desktop) {
      margin-top: 10px;
      background: white;
      border-radius: 5px;
      box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);
    }

    @include responsive (xs-mobile, mobile) {
      & > .c-form-group {
        & > .form:first-child {
          padding-top: 15px;
          & > .c-title { display: none; }
        }
      }
    }
  }

}
</style>
